/**
 * FibiConversaciones.jsx · v101.196.0 · Conversaciones de WhatsApp con Fibi (asistente virtual) y agendadores.
 * Bandeja por sucursal: clientes en espera (con el resumen de Fibi), mis chats, con Fibi y cerradas.
 * El agendador toma la conversación y sigue chateando con el cliente en su mismo WhatsApp desde aquí.
 * v101.197.0 · Stickers de la mascota robot (se ven en el chat y el agente puede enviarlos) y emojis rápidos.
 * Datos: /api/v1/fibi/*.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import WhatsAppTabs from "../components/WhatsAppTabs";
import api, { API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Bot, Clock, Download, Hand, Link2, Loader2, MessageSquare, RefreshCw, Search, Send, Settings,
  XCircle, Undo2, Sparkles } from "lucide-react";

const OUT = "-6px -6px 14px rgba(255,255,255,.95), 6px 6px 14px rgba(163,177,198,.55)";
const IN_SM = "inset -2px -2px 5px rgba(255,255,255,.95), inset 2px 2px 5px rgba(163,177,198,.55)";
const card = { padding: 18, borderRadius: 22, background: "#ECF0F5", boxShadow: OUT, display: "flex", flexDirection: "column", gap: 12, minWidth: 0 };
const input = { border: 0, padding: "9px 11px", borderRadius: 11, background: "#E4EAF0", boxShadow: IN_SM, font: "500 13px Inter", color: "#1E293B", width: "100%" };
const btn = (primary, danger) => ({
  display: "inline-flex", alignItems: "center", gap: 6, border: 0, cursor: "pointer", borderRadius: 11, padding: "8px 12px",
  font: "600 12.5px Inter", background: primary ? "#0A3747" : danger ? "#FEE2E2" : "#E4EAF0",
  color: primary ? "#FFF" : danger ? "#B91C1C" : "#0A3747", boxShadow: primary ? "none" : OUT,
});
const errMsg = (e, d) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : d);
const ESTADO = {
  en_espera: { t: "En espera", c: "#B45309", bg: "#FEF3C7" },
  humano: { t: "Con agente", c: "#1D4ED8", bg: "#DBEAFE" },
  bot: { t: "Con Fibi", c: "#047857", bg: "#D1FAE5" },
  cerrada: { t: "Cerrada", c: "#475569", bg: "#E2E8F0" },
};
const FILTROS = [["en_espera", "En espera"], ["mios", "Mis chats"], ["bot", "Con Fibi"], ["abiertas", "Abiertas"], ["cerrada", "Cerradas"]];
const STICKER = (n) => `${API}/v1/fibi/sticker/${n}.webp`;
const EMOJIS = ["😊", "👋", "🙏", "💚", "✅", "📅", "⏰", "🛠️", "📶", "🙌", "😉", "🚐"];
const hora = (v) => (v ? new Date(v).toLocaleString("es-EC", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");
const hace = (v) => {
  if (!v) return "";
  const m = Math.max(0, Math.round((Date.now() - new Date(v).getTime()) / 60000));
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`;
};

function Chip({ estado }) {
  const e = ESTADO[estado] || ESTADO.cerrada;
  return <span style={{ fontSize: 11, fontWeight: 700, color: e.c, background: e.bg, borderRadius: 999, padding: "2px 8px", whiteSpace: "nowrap" }}>{e.t}</span>;
}

function Burbuja({ m }) {
  if (m.direccion === "nota") {
    return <div style={{ alignSelf: "center", fontSize: 11, color: "#64748B", background: "#E2E8F0", borderRadius: 999, padding: "3px 10px", maxWidth: "90%", textAlign: "center" }}>{m.texto} · {hora(m.created_at)}</div>;
  }
  const cliente = m.autor === "cliente";
  const fibi = m.autor === "fibi";
  const st = /^\[sticker:([a-z_]+)\]$/.exec(m.texto || "");
  return (
    <div style={{ alignSelf: cliente ? "flex-start" : "flex-end", maxWidth: "78%" }}>
      <div style={{ fontSize: 10.5, color: "#64748B", margin: "0 6px 2px", textAlign: cliente ? "left" : "right" }}>
        {cliente ? "Cliente" : fibi ? "Fibi" : m.autor_nombre || "Agente"} · {hora(m.created_at)}
        {!cliente && m.estado && m.estado !== "sent" ? ` · ${m.estado === "read" ? "leído" : m.estado === "delivered" ? "entregado" : m.estado === "failed" ? "NO ENVIADO" : m.estado}` : ""}
      </div>
      {st ? <img src={STICKER(st[1])} alt={`sticker ${st[1]}`} width={120} height={120} style={{ display: "block", marginLeft: cliente ? 0 : "auto" }} /> : (
      <div style={{ display: "flex", gap: 6, alignItems: "flex-end", flexDirection: cliente ? "row" : "row-reverse" }}>
        {fibi && <img src={STICKER("avatar")} alt="Fibi" width={30} height={30} style={{ borderRadius: 999, background: "#E0E621", flexShrink: 0 }} />}
        <div style={{ whiteSpace: "pre-wrap", fontSize: 13.5, lineHeight: 1.45, padding: "9px 12px", borderRadius: 14,
          background: cliente ? "#FFFFFF" : fibi ? "#DCFCE7" : "#DBEAFE", color: "#0F172A",
          border: m.estado === "failed" ? "1px solid #DC2626" : "1px solid #E2E8F0" }}>
          {m.texto}
        </div>
      </div>)}
      {m.estado === "failed" && m.error && <div style={{ fontSize: 11, color: "#B91C1C", marginTop: 2 }}>{m.error}</div>}
    </div>
  );
}

function Config({ onClose }) {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/v1/fibi/config").then(({ data }) => setC(data)).catch(() => setC({})); }, []);
  if (!c) return <section style={card}><Loader2 className="animate-spin" size={18} /></section>;
  const set = (k, v) => setC({ ...c, [k]: v });
  const guardar = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/v1/fibi/config", {
        activo: !!c.activo, nombre: c.nombre, dias: Number(c.dias), capacidad_tecnico: Number(c.capacidad_tecnico),
        max_respuestas: Number(c.max_respuestas), antelacion_horas: Number(c.antelacion_horas), domingo: !!c.domingo,
        cierre_minutos: Number(c.cierre_minutos), recordatorio_minutos: Number(c.recordatorio_minutos), agenda_espera_minutos: Number(c.agenda_espera_minutos),
        dias_fuera_sla: Number(c.dias_fuera_sla ?? 3), ampliar_sucursal: c.ampliar_sucursal !== false,   // v101.224.0
        franjas: String(c._franjas ?? (c.franjas || []).map((f) => f.join("-")).join(", ")).split(",").map((x) => x.trim().split("-")).filter((f) => f.length === 2),
      });
      setC(data); toast.success("Configuración de Fibi guardada");
    } catch (e) { toast.error(errMsg(e, "No se pudo guardar")); } finally { setSaving(false); }
  };
  const ro = !c.puede_editar;
  const lab = { fontSize: 11.5, color: "#475569", fontWeight: 600 };
  return (
    <section style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: "#0A3747", margin: 0 }}>Configuración de Fibi</h2>
        <button style={btn()} onClick={onClose}>Cerrar</button>
      </div>
      {!c.ia_configurada && <div style={{ fontSize: 12, color: "#B91C1C" }}>Falta la clave de IA en el servidor: Fibi pasará todas las conversaciones a un agente.</div>}
      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, fontWeight: 600 }}>
        <input type="checkbox" disabled={ro} checked={!!c.activo} onChange={(e) => set("activo", e.target.checked)} />
        Fibi responde a los clientes (apagado: todo pasa directo a la cola de agendadores)
      </label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 }}>
        <div><div style={lab}>Nombre del asistente</div><input disabled={ro} style={input} value={c.nombre || ""} onChange={(e) => set("nombre", e.target.value)} /></div>
        <div><div style={lab}>Días que ofrece (mínimo, días hábiles)</div><input disabled={ro} type="number" min="1" max="10" style={input} value={c.dias} onChange={(e) => set("dias", e.target.value)} /></div>
        <div><div style={lab}>OTs por técnico y franja</div><input disabled={ro} type="number" min="1" max="6" style={input} value={c.capacidad_tecnico} onChange={(e) => set("capacidad_tecnico", e.target.value)} /></div>
        <div><div style={lab}>Horas de antelación</div><input disabled={ro} type="number" min="0" max="48" style={input} value={c.antelacion_horas} onChange={(e) => set("antelacion_horas", e.target.value)} /></div>
        <div><div style={lab}>Respuestas antes de pasar a agente</div><input disabled={ro} type="number" min="3" max="40" style={input} value={c.max_respuestas} onChange={(e) => set("max_respuestas", e.target.value)} /></div>
        <div><div style={lab}>Cerrar por inactividad (min)</div><input disabled={ro} type="number" min="5" max="1440" style={input} value={c.cierre_minutos} onChange={(e) => set("cierre_minutos", e.target.value)} /></div>
        <div><div style={lab}>Recordatorio de agenda cada (min)</div><input disabled={ro} type="number" min="1" max="120" style={input} value={c.recordatorio_minutos} onChange={(e) => set("recordatorio_minutos", e.target.value)} /></div>
        <div><div style={lab}>Agenda sin respuesta → agendador (min)</div><input disabled={ro} type="number" min="5" max="1440" style={input} value={c.agenda_espera_minutos} onChange={(e) => set("agenda_espera_minutos", e.target.value)} /></div>
        {/* v101.224.0 · sin cupo en el plazo: días hábiles siguientes que también ofrece (0 = pasa a un agente) */}
        <div><div style={lab}>Sin cupo: días siguientes que ofrece</div><input disabled={ro} type="number" min="0" max="7" style={input} value={c.dias_fuera_sla ?? 3} onChange={(e) => set("dias_fuera_sla", e.target.value)} /></div>
      </div>
      <div><div style={lab}>Franjas (HH:MM-HH:MM, separadas por coma)</div>
        <input disabled={ro} style={input} value={c._franjas ?? (c.franjas || []).map((f) => f.join("-")).join(", ")} onChange={(e) => set("_franjas", e.target.value)} /></div>
      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12.5 }}>
        <input type="checkbox" disabled={ro} checked={!!c.domingo} onChange={(e) => set("domingo", e.target.checked)} /> Ofrecer domingos
      </label>
      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12.5 }}>
        <input type="checkbox" disabled={ro} checked={c.ampliar_sucursal !== false} onChange={(e) => set("ampliar_sucursal", e.target.checked)} /> Si los técnicos de la zona no tienen espacio, ofrecer con los de la sucursal
      </label>
      <div style={{ fontSize: 11.5, color: "#64748B" }}>Fibi ofrece primero los horarios dentro del plazo SLA de la orden. Si no hay, ofrece los primeros disponibles de los «Días que ofrece» y luego de los días siguientes (marcados «fuera del SLA» en la OT). Solo pasa a un agente si no encuentra ninguno.</div>
      {!ro && <div><button style={btn(true)} disabled={saving} onClick={guardar}>{saving ? <Loader2 size={14} className="animate-spin" /> : <Settings size={14} />} Guardar</button></div>}
    </section>
  );
}

export function Chat({ cid, onChanged }) {   // v101.200.0 · también en la OT (ventana flotante)
  const { user } = useAuth() || {};
  const [d, setD] = useState(null);
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const [vinc, setVinc] = useState("");
  const [stk, setStk] = useState(false);
  const [lista, setLista] = useState([]);
  useEffect(() => { api.get("/v1/fibi/stickers").then(({ data }) => setLista(data.items || [])).catch(() => setLista([])); }, []);
  const fin = useRef(null);
  const nMsgs = useRef(0);
  const load = useCallback(() => api.get(`/v1/fibi/conversaciones/${cid}`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e, "No se pudo cargar"))), [cid]);
  useEffect(() => { setD(null); load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    const n = d?.mensajes?.length || 0;
    if (n !== nMsgs.current) { nMsgs.current = n; fin.current?.scrollIntoView({ block: "end" }); }
  }, [d]);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await load(); onChanged(); } catch (e) { toast.error(errMsg(e, "No se pudo completar")); } finally { setBusy(false); }
  };
  if (!d) return <section style={{ ...card, alignItems: "center", justifyContent: "center", minHeight: 420 }}><Loader2 className="animate-spin" /></section>;
  const c = d.conversacion;
  const ot = d.ot;
  const mio = c.estado === "humano" && String(c.asignado_id) === String(user?.id);
  const enviar = () => txt.trim() && run(async () => { await api.post(`/v1/fibi/conversaciones/${cid}/mensaje`, { texto: txt.trim() }, { noProcess: true }); setTxt(""); });
  return (
    <section style={{ ...card, padding: 0, gap: 0, overflow: "hidden", minHeight: 560 }}>
      <div style={{ padding: "14px 18px", borderBottom: "1px solid #D9E1EA", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontWeight: 800, color: "#0A3747", fontSize: 15 }}>{c.cliente || "Cliente sin nombre"}</span>
            <Chip estado={c.estado} />
            <span style={{ fontSize: 12, color: "#64748B" }}>tel. {c.telefono_corto}</span>
          </div>
          <div style={{ fontSize: 12, color: "#475569", marginTop: 3 }}>
            {ot ? <>OT <Link to={`/ot-smart/${ot.odoo_task_id}`} style={{ color: "#0F7FA8", fontWeight: 700 }}>{ot.task_number}</Link> · {ot.activity_type || "—"} · {ot.branch_name || "sin sucursal"}{ot.scheduled_at ? ` · cita ${hora(ot.scheduled_at)}` : ""}</> : "Sin OT vinculada"}
            {c.asignado_nombre ? ` · atiende ${c.asignado_nombre}` : ""}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {c.estado !== "cerrada" && !mio && <button style={btn(true)} disabled={busy} onClick={() => run(() => api.post(`/v1/fibi/conversaciones/${cid}/tomar`, {}, { noProcess: true }), "Conversación tomada")}><Hand size={14} /> Tomar</button>}
          {c.estado !== "cerrada" && c.estado !== "bot" && <button style={btn()} disabled={busy} onClick={() => run(() => api.post(`/v1/fibi/conversaciones/${cid}/devolver`, {}, { noProcess: true }), "Devuelta a Fibi")}><Undo2 size={14} /> Devolver a Fibi</button>}
          {c.estado !== "cerrada" && <button style={btn(false, true)} disabled={busy} onClick={() => { const nota = window.prompt("Nota de cierre (opcional)") ; if (nota !== null) run(() => api.post(`/v1/fibi/conversaciones/${cid}/cerrar`, { nota }, { noProcess: true }), "Conversación cerrada y guardada en la OT"); }}><XCircle size={14} /> Cerrar</button>}
          {ot && <button style={btn()} onClick={async () => { try { const r = await api.get(`/v1/fibi/ot/${ot.odoo_task_id}/evidencia.pdf`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = `WhatsApp_OT_${ot.task_number}.pdf`; a.click(); URL.revokeObjectURL(u); } catch (er) { toast.error("No se pudo descargar"); } }}><Download size={14} /> PDF</button>}
        </div>
      </div>
      {(c.resumen || c.motivo) && (
        <div style={{ margin: "12px 18px 0", padding: "10px 12px", borderRadius: 12, background: "#FEF3C7", fontSize: 12.5, color: "#78350F" }}>
          <b>{c.tipo === "falla" ? "Falla reportada" : c.tipo === "agenda" ? "Agenda pendiente" : "Motivo"}:</b> {c.motivo || "—"}{c.resumen ? <><br /><b>Resumen de Fibi:</b> {c.resumen}</> : null}
        </div>
      )}
      {!ot && c.estado !== "cerrada" && (
        <div style={{ margin: "12px 18px 0", display: "flex", gap: 8 }}>
          <input style={{ ...input, maxWidth: 220 }} placeholder="Número de OT a vincular" value={vinc} onChange={(e) => setVinc(e.target.value)} />
          <button style={btn()} disabled={busy || !vinc.trim()} onClick={() => run(() => api.post(`/v1/fibi/conversaciones/${cid}/vincular`, { ot: vinc.trim() }, { noProcess: true }), "OT vinculada")}><Link2 size={14} /> Vincular</button>
          {d.otras_ots?.length > 0 && <span style={{ fontSize: 11.5, color: "#64748B", alignSelf: "center" }}>OTs del teléfono: {d.otras_ots.map((o) => o.task_number).join(", ")}</span>}
        </div>
      )}
      <div style={{ flex: 1, overflowY: "auto", padding: "14px 18px", display: "flex", flexDirection: "column", gap: 10, background: "#F1F5F9", margin: 12, borderRadius: 16, maxHeight: 520 }}>
        {d.mensajes.map((m) => <Burbuja key={m.id} m={m} />)}
        <div ref={fin} />
      </div>
      <div style={{ padding: "0 18px 16px" }}>
        {mio && d.ventana_24h && (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center", marginBottom: 6 }}>
            {EMOJIS.map((e) => <button key={e} title="Agregar emoji" onClick={() => setTxt((t) => `${t}${e}`)} style={{ border: 0, background: "transparent", cursor: "pointer", fontSize: 18, padding: 2 }}>{e}</button>)}
            <button style={{ ...btn(stk), padding: "5px 10px", marginLeft: 6 }} onClick={() => setStk(!stk)}>Stickers</button>
          </div>
        )}
        {mio && d.ventana_24h && stk && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
            {lista.map((x) => (
              <button key={x.nombre} title={x.descripcion} disabled={busy} onClick={() => run(async () => { await api.post(`/v1/fibi/conversaciones/${cid}/sticker`, { nombre: x.nombre }, { noProcess: true }); setStk(false); })}
                style={{ border: 0, background: "#FFFFFF", borderRadius: 12, padding: 4, cursor: "pointer" }}>
                <img src={STICKER(x.nombre)} alt={x.nombre} width={64} height={64} />
              </button>
            ))}
          </div>
        )}
        {mio && d.ventana_24h && (
          <div style={{ display: "flex", gap: 8 }}>
            <textarea rows={2} style={{ ...input, resize: "vertical" }} placeholder="Escriba al cliente… (Enter envía, Shift+Enter nueva línea)" value={txt}
              onChange={(e) => setTxt(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviar(); } }} />
            <button style={{ ...btn(true), alignSelf: "stretch" }} disabled={busy || !txt.trim()} onClick={enviar}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Enviar</button>
          </div>
        )}
        {mio && !d.ventana_24h && (
          <div style={{ fontSize: 12.5, color: "#92400E", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            Pasaron más de 24 horas desde el último mensaje del cliente: WhatsApp solo permite volver a escribirle con el saludo aprobado.
            <button style={btn()} disabled={busy || !ot} onClick={() => run(() => api.post(`/v1/fibi/conversaciones/${cid}/saludo`, {}, { noProcess: true }), "Saludo enviado")}><Sparkles size={14} /> Enviar saludo</button>
          </div>
        )}
        {!mio && c.estado !== "cerrada" && <div style={{ fontSize: 12.5, color: "#64748B" }}>{c.estado === "bot" ? "Fibi está atendiendo. Pulse «Tomar» para continuar usted." : c.estado === "humano" ? `La atiende ${c.asignado_nombre}.` : "Pulse «Tomar» para atender al cliente."}</div>}
        {c.estado === "cerrada" && <div style={{ fontSize: 12.5, color: "#64748B" }}>Conversación cerrada{c.cerrada_por ? ` por ${c.cerrada_por}` : ""}; quedó guardada como evidencia en la OT.</div>}
      </div>
    </section>
  );
}

export default function FibiConversaciones() {
  const { user } = useAuth() || {};
  const [params, setParams] = useSearchParams();
  const [filtro, setFiltro] = useState("en_espera");
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [cfg, setCfg] = useState(false);
  const [nuevaOt, setNuevaOt] = useState("");
  const [iniciando, setIniciando] = useState(false);
  const sel = params.get("c");
  const load = useCallback(async () => {
    try {
      const est = filtro === "mios" ? "humano" : filtro;
      const { data } = await api.get("/v1/fibi/conversaciones", { params: { estado: est, q: q || undefined } });
      setItems(filtro === "mios" ? data.items.filter((x) => String(x.asignado_id) === String(user?.id)) : data.items);
    } catch (e) { setItems([]); toast.error(errMsg(e, "No se pudieron cargar las conversaciones")); }
  }, [filtro, q, user?.id]);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  const abrir = (id) => setParams(id ? { c: String(id) } : {});
  const iniciar = async () => {
    setIniciando(true);
    try {
      const { data } = await api.post("/v1/fibi/iniciar", { ot: nuevaOt.trim() }, { noProcess: true });
      toast.success("Fibi saludó al cliente por WhatsApp"); setNuevaOt(""); setFiltro("abiertas"); abrir(data.conversacion);
    } catch (e) { toast.error(errMsg(e, "No se pudo iniciar")); } finally { setIniciando(false); }
  };
  const lista = useMemo(() => items || [], [items]);
  return (
    <Layout title="Conversaciones WhatsApp" subtitle="FIBI · AGENDAMIENTO Y SERVICIO AL CLIENTE" wide>
      <div style={{ maxWidth: 1500, margin: "0 auto" }}>
        <WhatsAppTabs />
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
          {FILTROS.map(([k, t]) => (
            <button key={k} style={{ ...btn(filtro === k), padding: "7px 12px" }} onClick={() => setFiltro(k)}>{t}</button>
          ))}
          <span style={{ position: "relative", minWidth: 220, flex: "0 1 260px" }}>
            <Search size={14} color="#64748B" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
            <input style={{ ...input, paddingLeft: 30 }} placeholder="Cliente, OT o teléfono" value={q} onChange={(e) => setQ(e.target.value)} />
          </span>
          <button style={btn()} onClick={load}><RefreshCw size={14} /></button>
          <span style={{ flex: 1 }} />
          <input style={{ ...input, width: 170 }} placeholder="N.º de OT" value={nuevaOt} onChange={(e) => setNuevaOt(e.target.value)} />
          <button style={btn(true)} disabled={iniciando || !nuevaOt.trim()} onClick={iniciar}>{iniciando ? <Loader2 size={14} className="animate-spin" /> : <Bot size={14} />} Contactar con Fibi</button>
          <button style={btn()} onClick={() => setCfg(!cfg)}><Settings size={14} /></button>
        </div>
        {cfg && <div style={{ marginBottom: 14 }}><Config onClose={() => setCfg(false)} /></div>}
        <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 380px) 1fr", gap: 16, alignItems: "start" }}>
          <section style={{ ...card, padding: 10, maxHeight: 720, overflowY: "auto" }}>
            {items === null && <Loader2 className="animate-spin" size={18} />}
            {items !== null && lista.length === 0 && <div style={{ fontSize: 13, color: "#64748B", padding: 12 }}>No hay conversaciones en esta vista.</div>}
            {lista.map((x) => (
              <button key={x.id} onClick={() => abrir(x.id)} style={{ textAlign: "left", border: 0, cursor: "pointer", borderRadius: 14, padding: "10px 12px",
                background: String(x.id) === sel ? "#DBEAFE" : "transparent", display: "flex", flexDirection: "column", gap: 3 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 6, alignItems: "center" }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.cliente || `Tel. ${x.telefono_corto}`}</span>
                  <Chip estado={x.estado} />
                </div>
                <div style={{ fontSize: 11.5, color: "#475569" }}>{x.task_number ? `OT ${x.task_number}` : "Sin OT"} · {x.branch_name || "sin sucursal"}</div>
                {x.estado === "en_espera" && <div style={{ fontSize: 11.5, color: "#B45309", display: "flex", gap: 4, alignItems: "center" }}><Clock size={12} /> espera {hace(x.espera_desde)} · {x.motivo}</div>}
                <div style={{ fontSize: 12, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.ultimo_texto}</div>
              </button>
            ))}
          </section>
          {sel ? <Chat key={sel} cid={sel} onChanged={load} /> : (
            <section style={{ ...card, alignItems: "center", justifyContent: "center", minHeight: 420, color: "#64748B", textAlign: "center" }}>
              <MessageSquare size={30} />
              <div style={{ fontSize: 14, fontWeight: 600 }}>Elija una conversación</div>
              <div style={{ fontSize: 12.5, maxWidth: 420 }}>Fibi atiende sola a los clientes. Cuando no puede resolver, la conversación aparece «En espera» en su sucursal con el resumen del problema.</div>
            </section>
          )}
        </div>
      </div>
    </Layout>
  );
}
